import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthApiService } from '../../auth/api/auth-api.service';
import { LoginPayload } from '../../auth/models/auth.models';
import { pipe, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { TuiAlertService } from '@taiga-ui/core';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = environment.storagePrefix + 'token';

interface AuthState {
  token: string | null;
  isAuthenticating: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: null,
  isAuthenticating: false,
  error: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    isAuthenticated: computed<boolean>(() => !!store.token()),
  })),
  withMethods((store) => {
    const api = inject(AuthApiService);
    const router = inject(Router);
    const alert = inject(TuiAlertService);

    return {
      login: rxMethod<LoginPayload>(
        pipe(
          tap(() => patchState(store, { isAuthenticating: true })),
          switchMap((payload) =>
            api.login(payload).pipe(
              tapResponse({
                next: (res) => {
                  const token = res.token;
                  localStorage.setItem(STORAGE_KEY, token);
                  patchState(store, { token });
                  const queryParams = router.parseUrl(router.url).queryParams;
                  router.navigate(['/'], { queryParams });
                },
                error: (err: any) => {
                  const message = err.error.errors[0].message;
                  alert.open(message, { appearance: 'negative' }).subscribe();
                  patchState(store, { error: message });
                },
                finalize: () => patchState(store, { isAuthenticating: false }),
              }),
            ),
          ),
        ),
      ),
      logout(): void {
        localStorage.removeItem(STORAGE_KEY);
        patchState(store, { token: null });
        router.navigate(['auth/login']);
      },
    };
  }),
  withHooks({
    onInit(store) {
      const token = localStorage.getItem(STORAGE_KEY);
      if (token) patchState(store, { token });
    },
  }),
);
